import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed view state, extracted from the convention `components/chains/ChainList.tsx`
 * established: state is read straight from the URL (no local mirror, no sync effect), updates
 * use `replace: true` so filtering does not fill the back button, every other param survives,
 * and a param at its default value is deleted rather than written empty — so a page in its
 * default state has a clean, shareable URL.
 */

/** Value to write for a param: a string/number, or null to delete it. */
export type UrlParamValue = string | number | null;

/**
 * Write several params in one navigation. Necessary, not convenient: react-router hands the
 * functional `setSearchParams` updater the params captured at render time, so two setter calls
 * inside one event handler would silently discard the first one's write.
 */
export function useSetUrlParams(): (updates: Record<string, UrlParamValue>) => void {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (updates: Record<string, UrlParamValue>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [name, value] of Object.entries(updates)) {
            const asString = value === null ? '' : String(value);
            if (asString === '') {
              next.delete(name);
            } else {
              next.set(name, asString);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
}

/**
 * A single string (or enum) search param. `allowed`, when given, is validated on read: an
 * unknown value — a hand-edited link, or one saved before an option was renamed — falls back to
 * the default rather than putting the page into a state it has no rendering for.
 */
export function useUrlParam(name: string, defaultValue: string): [string, (next: string) => void];
export function useUrlParam<T extends string>(
  name: string,
  defaultValue: T,
  allowed: readonly T[],
): [T, (next: T) => void];
export function useUrlParam<T extends string>(
  name: string,
  defaultValue: T,
  allowed?: readonly T[],
): [T, (next: T) => void] {
  const [searchParams] = useSearchParams();
  const setParams = useSetUrlParams();

  const raw = searchParams.get(name);
  const value: T =
    raw === null || raw === '' || (allowed !== undefined && !allowed.includes(raw as T))
      ? defaultValue
      : (raw as T);

  const set = useCallback(
    (next: T) => {
      setParams({ [name]: next === defaultValue ? null : next });
    },
    [setParams, name, defaultValue],
  );

  return [value, set];
}

export interface UrlNumberOptions {
  /** Values below this read as the default. */
  min?: number;
  /** Fractional values read as the default. */
  integer?: boolean;
}

/**
 * A numeric search param (page numbers). Anything the caller's bounds reject — non-numeric,
 * fractional, below `min` — reads as the default, so `?page=0` or `?page=2.5` opens page 1
 * rather than asking the API for a page that does not exist.
 */
export function useUrlNumber(
  name: string,
  defaultValue: number,
  options: UrlNumberOptions = {},
): [number, (next: number) => void] {
  const [searchParams] = useSearchParams();
  const setParams = useSetUrlParams();
  const { min, integer } = options;

  const isValid = useCallback(
    (candidate: number) =>
      Number.isFinite(candidate) &&
      (!integer || Number.isInteger(candidate)) &&
      (min === undefined || candidate >= min),
    [integer, min],
  );

  const raw = searchParams.get(name);
  const parsed = raw === null || raw.trim() === '' ? Number.NaN : Number(raw);
  const value = isValid(parsed) ? parsed : defaultValue;

  const set = useCallback(
    (next: number) => {
      setParams({ [name]: next === defaultValue || !isValid(next) ? null : next });
    },
    [setParams, name, defaultValue, isValid],
  );

  return [value, set];
}

/**
 * A free-text search param that keeps up with typing but only reaches the URL once the typing
 * stops. The local value is what the input renders; the URL value wins on mount and on
 * back/forward, so a shared link still opens on the filter it names.
 *
 * Every free-text filter uses this — one convention, so no field re-navigates per keystroke.
 */
export function useDebouncedUrlParam(
  name: string,
  defaultValue: string,
  delayMs = 400,
  options: { resets?: readonly string[] } = {},
): [string, (next: string) => void] {
  const [urlValue] = useUrlParam(name, defaultValue);
  const setParams = useSetUrlParams();
  const [localValue, setLocalValue] = useState(urlValue);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const { resets } = options;

  // The URL is the source of truth: adopt it whenever it changes underneath us (a link, the
  // back button, another control clearing this one).
  useEffect(() => {
    // A keystroke's pending write must die here too: type, hit Back inside the delay, and the
    // orphaned timer would otherwise replace the restored URL with the typed value — a silent
    // undo of the Back with no history entry to return to.
    clearTimeout(timerRef.current);
    setLocalValue(urlValue);
  }, [urlValue]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const set = useCallback(
    (next: string) => {
      setLocalValue(next);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const updates: Record<string, UrlParamValue> = {
          [name]: next === defaultValue ? null : next,
        };
        // `resets` names the params this filter invalidates — a page number, typically, since
        // page 7 of the unfiltered list is not page 7 of the filtered one. They go out in the
        // same write as the filter itself.
        for (const reset of resets ?? []) updates[reset] = null;
        setParams(updates);
      }, delayMs);
    },
    [setParams, name, defaultValue, delayMs, resets],
  );

  return [localValue, set];
}
